/**
 * Whether the shell is running inside the P18 native container rather than a browser.
 *
 * **The honest statement is not "we are installed" — it is "installation is not a thing here."**
 * That distinction is the whole reason this is its own predicate rather than a wider
 * `isStandalone`. A native build *is* the installed app, but it arrives from a store, it cannot be
 * added to a home screen, and there is no Share sheet to point anyone at. Folding it into the
 * display-mode question would make `isStandalone` mean two different things and would quietly
 * change what every other reader of that flag is told.
 *
 * **What went wrong without it.** `useInstallPrompt`'s `canInstall` is
 * `!isStandalone && (platform === 'ios' || deferred !== null)`, and inside a WKWebView every term
 * falls the wrong way: the user agent says `iPhone`, `matchMedia('(display-mode: standalone)')` is
 * false, and `navigator.standalone` is a **Safari-only** property that is `undefined` there. So the
 * iOS container launched straight into the sign-in screen with an add-to-home-screen card over it —
 * offering to install an app that was already installed, via a sheet that does not exist — plus a
 * dead drawer row and a dot on the app bar. It is the dead affordance D154 was raised to remove,
 * arriving through a predicate instead of a service worker.
 *
 * **Android never showed it, which is why a full Android device pass could not have found it**: that
 * branch needs `beforeinstallprompt`, and no WebView fires it. iOS is the only branch that reaches
 * install chrome without an event.
 *
 * **Read the injected global, never a dependency.** `web/` must not import `@capacitor/core` — the
 * shell is P6's and the container is P18's, and `native/` consumes the built bundle rather than the
 * other way round. Capacitor's bridge injects this global into the WebView before app code runs, so
 * duck-typing it costs nothing and keeps the dependency edge pointing one way.
 *
 * **Do not "simplify" this to a `capacitor:` protocol check.** That scheme is iOS-only: P18 sets
 * `androidScheme: 'https'` deliberately, so the Android container's origin is byte-identical to the
 * web origin (D161(c)) and a protocol test would report Android as a browser — reintroducing the
 * bug on the one platform that never had it, while looking like a tidy-up.
 */
interface CapacitorBridge {
  isNativePlatform?: () => boolean
}

export function isNativeContainer(): boolean {
  const bridge = (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
  return typeof bridge?.isNativePlatform === 'function' && bridge.isNativePlatform() === true
}
